import { useMemo } from "react";
import { Box, Table, Thead, Tbody, Tr, Th, Td, Text, Badge, HStack } from "@chakra-ui/react";
import { parseDelimited, sniffDelimiter, parseEdi, prettyXml } from "./parsers";

const MONO = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";
const MAX_ROWS = 5000; // a browser table of more rows than this is unusable; say so instead

export function TextPreview({ text }: { text: string }) {
  return (
    <Box as="pre" p={3} h="100%" overflow="auto" whiteSpace="pre-wrap" wordBreak="break-word"
      fontFamily={MONO} fontSize="13px" lineHeight="1.5">{text}</Box>
  );
}

export function XmlPreview({ text }: { text: string }) {
  const pretty = useMemo(() => prettyXml(text), [text]);
  return <TextPreview text={pretty} />;
}

export function CsvPreview({ text }: { text: string }) {
  const { header, rows, total } = useMemo(() => {
    const all = parseDelimited(text, sniffDelimiter(text)).filter((r) => r.length > 1 || r[0] !== "");
    return { header: all[0] ?? [], rows: all.slice(1, MAX_ROWS + 1), total: Math.max(0, all.length - 1) };
  }, [text]);
  return (
    <Box h="100%" overflow="auto">
      {total > MAX_ROWS && (
        <Text p={2} fontSize="sm" color="orange.600">Showing the first {MAX_ROWS.toLocaleString()} of {total.toLocaleString()} rows. Download the file for the rest.</Text>
      )}
      <Table size="sm" variant="simple">
        <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
          <Tr><Th>#</Th>{header.map((h, i) => <Th key={i} textTransform="none">{h}</Th>)}</Tr>
        </Thead>
        <Tbody>
          {rows.map((r, i) => (
            <Tr key={i}><Td color="gray.500">{i + 1}</Td>{header.map((_, c) => <Td key={c} whiteSpace="nowrap">{r[c] ?? ""}</Td>)}</Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export function EdiPreview({ text }: { text: string }) {
  const { dialect, segments } = useMemo(() => parseEdi(text), [text]);
  return (
    <Box h="100%" overflow="auto" p={3} fontFamily={MONO} fontSize="13px">
      <HStack mb={3} fontFamily="body">
        <Badge colorScheme={dialect === "unknown" ? "gray" : "blue"}>{dialect}</Badge>
        <Text fontSize="sm" color="gray.600">{segments.length.toLocaleString()} segments</Text>
      </HStack>
      {segments.map((s, i) => (
        <Box key={i} pl={`${s.indent * 20}px`} py="1px">
          <Box as="span" fontWeight="bold" color="blue.600" mr={2}>{s.tag}</Box>
          {s.elements.map((e, j) => (
            <Box as="span" key={j} mr={2}>
              <Box as="sup" color="gray.400" fontSize="9px" mr="1px">{String(j + 1).padStart(2, "0")}</Box>{e || "·"}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
